// Delivery failure error codes mapping & human-friendly descriptions (WP-21).
// Translates raw Meta Cloud API / sendCore rejection codes into clear explanations.

import type { InboxMessageError } from './inboxTypes';

export interface DeliveryErrorInfo {
  code: string;
  reasonKey: string;
  defaultMessage: string;
}

export function parseDeliveryErrorCode(rawCode?: string | null): string {
  if (!rawCode) return '';
  const match = String(rawCode).match(/(\d{5,6})/);
  return match ? match[1] : String(rawCode).trim();
}

/**
 * Maps known Meta error codes to localized translation keys and default English explanations.
 */
export function getDeliveryErrorInfo(error?: InboxMessageError | null): DeliveryErrorInfo {
  const code = parseDeliveryErrorCode(error?.code);

  switch (code) {
    case '131042':
      return {
        code: '131042',
        reasonKey: 'inbox.failed.billing',
        defaultMessage: 'Payment issue on WhatsApp Business Account or credit limit exceeded.',
      };
    case '131026':
      return {
        code: '131026',
        reasonKey: 'inbox.failed.unreachable',
        defaultMessage: 'Recipient is not registered on WhatsApp or phone is unreachable.',
      };
    case '131047':
      return {
        code: '131047',
        reasonKey: 'inbox.failed.windowExpired',
        defaultMessage: '24-hour customer window expired. You must send an approved template.',
      };
    case '132012':
      return {
        code: '132012',
        reasonKey: 'inbox.failed.templateMismatch',
        defaultMessage: 'Template parameter mismatch or invalid variable values.',
      };
    case '131049':
      return {
        code: '131049',
        reasonKey: 'inbox.failed.engagementLimit',
        defaultMessage: 'Message blocked by Meta due to recipient protection/engagement limit.',
      };
    default:
      return {
        code: error?.code || 'failed',
        reasonKey: 'inbox.failed.generic',
        defaultMessage: error?.message || 'Message could not be delivered.',
      };
  }
}

/**
 * Returns the localized delivery failure reason string.
 */
export function getDeliveryFailureReason(
  error: InboxMessageError | null | undefined,
  t: (key: string) => string,
): string {
  if (!error) return t('inbox.failed.generic');

  const info = getDeliveryErrorInfo(error);
  const localized = t(info.reasonKey);

  // If translation returned key or fallback and raw message exists, prefer localized or raw message
  if (localized && localized !== info.reasonKey) {
    return localized;
  }

  return error.message || info.defaultMessage;
}
